/*
 * B2B Quick Order Collection View
 * Custom element <quick-order-collection>.
 *
 * - Holds typed quantities in a global object keyed by section id -> variant id,
 *   so values survive Section-Rendering-API section swaps and load-more appends.
 * - One delegated listener per element (render-volume mitigation).
 * - Per-row and page-wide add-to-cart via items[] POST to the cart add route.
 */

window.MinimogQuickOrderState = window.MinimogQuickOrderState || {};

if (!customElements.get("quick-order-collection")) {
  class QuickOrderCollection extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
      this.strings = this._readStrings();
      this._onListUpdated = this._onListUpdated.bind(this);
    }

    connectedCallback() {
      if (!window.MinimogQuickOrderState[this.sectionId]) {
        window.MinimogQuickOrderState[this.sectionId] = {};
      }
      this.state = window.MinimogQuickOrderState[this.sectionId];

      this.addEventListener("click", this._onClick.bind(this));
      this.addEventListener("input", this._onInput.bind(this));
      this.addEventListener("change", this._onInput.bind(this));

      // Load-more appends new rows into the existing element.
      if (window.MinimogEvents && window.MinimogEvents.subscribe) {
        window.MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", this._onListUpdated);
      }

      this._restoreAll();
      this._refreshSummary();
    }

    disconnectedCallback() {
      if (window.MinimogEvents && window.MinimogEvents.unSubscribe) {
        window.MinimogEvents.unSubscribe("ON_PRODUCT_LIST_UPDATED", this._onListUpdated);
      }
    }

    /* ---------------------------------------------------------------- setup */

    _readStrings() {
      const fallback = {
        error_inventory: "Only %COUNT% available. Reduce the quantity to continue.",
        error_min: "Minimum order is %COUNT%.",
        error_max: "Maximum order is %COUNT%.",
        error_increment: "Enter a multiple of %COUNT%.",
        error_unavailable: "This item is unavailable.",
        error_network: "Something went wrong. Please try again.",
        items_count: "%COUNT% items",
      };
      const node = this.querySelector("[data-qo-strings]");
      if (!node) return fallback;
      try {
        return Object.assign(fallback, JSON.parse(node.textContent));
      } catch (e) {
        return fallback;
      }
    }

    _onListUpdated() {
      this._restoreAll();
      this._refreshSummary();
    }

    _restoreAll() {
      this.querySelectorAll("[data-qo-input]").forEach((input) => {
        const id = input.dataset.variantId;
        if (this.state[id] != null) {
          input.value = this.state[id];
        }
        this._validateInput(input);
      });
      this.querySelectorAll("[data-qo-row]").forEach((row) => this._refreshRow(row));
    }

    /* --------------------------------------------------------------- events */

    _onClick(e) {
      const step = e.target.closest("[data-qo-step]");
      if (step) return this._onStep(step);

      const add = e.target.closest("[data-qo-add]");
      if (add) return this._onRowAdd(add);

      const addAll = e.target.closest("[data-qo-add-all]");
      if (addAll) return this._onAddAll(addAll);

      const showAll = e.target.closest("[data-qo-show-all]");
      if (showAll) return this._onShowAll(showAll);

      const thumb = e.target.closest("[data-qo-thumb]");
      if (thumb) return this._onThumb(thumb);
    }

    _onInput(e) {
      const input = e.target.closest("[data-qo-input]");
      if (!input) return;
      this._validateInput(input);
      const value = this._parse(input.value);
      const id = input.dataset.variantId;
      if (value > 0) {
        this.state[id] = value;
      } else {
        delete this.state[id];
      }
      this._refreshRow(input.closest("[data-qo-row]"));
      this._refreshSummary();
    }

    _onStep(button) {
      const qty = button.closest(".m-quantity");
      const input = qty && qty.querySelector("[data-qo-input]");
      if (!input) return;

      const min = parseInt(input.dataset.min, 10) || 0;
      const max = input.dataset.max ? parseInt(input.dataset.max, 10) : null;
      const stepAttr = parseInt(input.getAttribute("step"), 10) || 1;
      let value = this._parse(input.value);

      if (button.dataset.qoStep === "up") {
        if (value === 0 && min > 1) {
          value = min;
        } else {
          value = value + stepAttr;
        }
        if (max != null && value > max) value = max;
      } else {
        const next = value - stepAttr;
        if (value > 0 && min > 1 && value <= min) {
          value = 0;
        } else {
          value = next < 0 ? 0 : next;
        }
      }
      input.value = value;
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }

    _onShowAll(button) {
      const row = button.closest("[data-qo-row]");
      if (!row) return;
      row.querySelectorAll(".m-quick-order__vline--hidden").forEach((line) => {
        line.classList.remove("m-quick-order__vline--hidden");
        line.hidden = false;
      });
      button.remove();
    }

    _onThumb(button) {
      const row = button.closest("[data-qo-row]");
      const main = row && row.querySelector("[data-qo-media-main] img");
      const src = button.dataset.qoThumbSrc;
      if (main && src) {
        main.srcset = "";
        main.src = src;
      }
      const gallery = button.closest("[data-qo-gallery]");
      if (gallery) {
        gallery.querySelectorAll("[data-qo-thumb]").forEach((t) => t.classList.remove("is-active"));
      }
      button.classList.add("is-active");
    }

    /* ----------------------------------------------------------- validation */

    _parse(value) {
      const n = parseInt(value, 10);
      return isNaN(n) || n < 0 ? 0 : n;
    }

    _validateInput(input) {
      const value = this._parse(input.value);
      const min = parseInt(input.dataset.min, 10) || 0;
      const max = input.dataset.max ? parseInt(input.dataset.max, 10) : null;
      const step = parseInt(input.getAttribute("step"), 10) || 1;

      let message = "";
      if (value > 0) {
        if (min > 1 && value < min) {
          message = this._msg("error_min", min);
        } else if (step > 1 && value % step !== 0) {
          message = this._msg("error_increment", step);
        } else if (max != null && value > max) {
          message = this._msg("error_max", max);
        }
      }
      this._setLineError(input, message);
      return message === "";
    }

    _setLineError(input, message) {
      const line = input.closest("[data-qo-variant]");
      if (!line) return;
      const errorRow = line.nextElementSibling;
      const errorNode = errorRow && errorRow.matches("[data-qo-line-error-row]")
        ? errorRow.querySelector("[data-qo-line-error]")
        : null;
      if (message) {
        line.classList.add("has-error");
        if (errorRow) errorRow.hidden = false;
        if (errorNode) errorNode.textContent = message;
      } else {
        line.classList.remove("has-error");
        if (errorRow) errorRow.hidden = true;
        if (errorNode) errorNode.textContent = "";
      }
    }

    _msg(key, n) {
      return (this.strings[key] || "").replace("%COUNT%", n);
    }

    /* --------------------------------------------------------- row/summary */

    _rowItems(row) {
      const items = [];
      let hasError = false;
      row.querySelectorAll("[data-qo-input]").forEach((input) => {
        const value = this._parse(input.value);
        if (value > 0) {
          if (!this._validateInput(input)) hasError = true;
          items.push({ id: input.dataset.variantId, quantity: value });
        }
      });
      return { items, hasError };
    }

    _refreshRow(row) {
      if (!row) return;
      const button = row.querySelector("[data-qo-add]");
      if (!button) return;
      const { items, hasError } = this._rowItems(row);
      button.disabled = items.length === 0 || hasError;
    }

    _refreshSummary() {
      const summary = this.querySelector("[data-qo-summary]");
      const addAll = this.querySelector("[data-qo-add-all]");
      let count = 0;
      let total = 0;
      let anyError = false;

      this.querySelectorAll("[data-qo-input]").forEach((input) => {
        const value = this._parse(input.value);
        if (value > 0) {
          count += value;
          total += value * (parseInt(input.dataset.price, 10) || 0);
          if (input.closest("[data-qo-variant]").classList.contains("has-error")) anyError = true;
        }
      });

      if (summary) {
        summary.hidden = count === 0;
        const countNode = summary.querySelector("[data-qo-summary-count]");
        const totalNode = summary.querySelector("[data-qo-summary-total]");
        if (countNode) countNode.textContent = this._msg("items_count", count);
        if (totalNode) totalNode.textContent = this._formatMoney(total);
      }
      if (addAll) addAll.disabled = count === 0 || anyError;
    }

    /* --------------------------------------------------------------- cart */

    _onRowAdd(button) {
      const row = button.closest("[data-qo-row]");
      const { items, hasError } = this._rowItems(row);
      const errorNode = row.querySelector("[data-qo-row-error]");
      if (errorNode) errorNode.textContent = "";
      if (hasError || items.length === 0) return;
      this._addItems(items, button, errorNode, row);
    }

    _onAddAll(button) {
      const items = [];
      let hasError = false;
      this.querySelectorAll("[data-qo-input]").forEach((input) => {
        const value = this._parse(input.value);
        if (value > 0) {
          if (!this._validateInput(input)) hasError = true;
          items.push({ id: input.dataset.variantId, quantity: value });
        }
      });
      const summary = this.querySelector("[data-qo-summary]");
      const errorNode = summary && summary.querySelector("[data-qo-summary-error]");
      if (hasError || items.length === 0) return;
      this._addItems(items, button, errorNode, null);
    }

    _addItems(items, button, errorNode, row) {
      if (button.classList.contains("is-loading")) return;
      this._toggleLoading(button, true);

      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } })
      );

      const config = typeof fetchConfig === "function"
        ? fetchConfig("javascript")
        : { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/javascript" } };
      config.method = "POST";
      config.body = JSON.stringify({ items, sections, sections_url: window.location.pathname });

      const routes = (window.MinimogSettings && window.MinimogSettings.routes) || {};
      const addUrl = routes.cart_add_url || "/cart/add.js";

      fetch(addUrl, config)
        .then((res) => res.json())
        .then((response) => {
          if (response.status) {
            this._showError(this._mapError(response), errorNode, row);
            document.dispatchEvent(
              new CustomEvent("product-ajax:error", { detail: { errorMessage: response.description } })
            );
            return;
          }
          this._afterAdd(response, items);
        })
        .catch(() => {
          this._showError(this.strings.error_network, errorNode, row);
        })
        .finally(() => this._toggleLoading(button, false));
    }

    _afterAdd(response, items) {
      const cartDrawer = document.querySelector("m-cart-drawer");
      const settings = window.MinimogSettings || {};
      const strings = window.MinimogStrings || {};

      if (settings.use_ajax_atc !== false) {
        if (cartDrawer && settings.enable_cart_drawer && window.MinimogTheme && MinimogTheme.Notification) {
          MinimogTheme.Notification.show({
            target: cartDrawer.querySelector("m-cart-items"),
            method: "prepend",
            type: "success",
            message: strings.itemAdded || "Item added to cart",
            delay: 400,
          });
          if (typeof cartDrawer.open === "function") cartDrawer.open();
        }
        if (window.MinimogEvents && window.MinimogEvents.emit) {
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { source: "quick-order-collection", cart: response });
        }
        document.dispatchEvent(new CustomEvent("product-ajax:added", { detail: { product: response } }));

        // Reset the added inputs to the empty state.
        items.forEach(({ id }) => {
          delete this.state[id];
          const input = this.querySelector(`[data-qo-input][data-variant-id="${id}"]`);
          if (input) {
            input.value = 0;
            this._setLineError(input, "");
          }
        });
        this.querySelectorAll("[data-qo-row]").forEach((row) => this._refreshRow(row));
        this._refreshSummary();
      } else {
        window.location = (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.cart) || "/cart";
      }
    }

    _mapError(response) {
      const desc = (response.description || response.message || "").toString();
      const lower = desc.toLowerCase();
      if (lower.includes("stock") || lower.includes("available") || lower.includes("inventory")) {
        // Prefer Shopify's own count-bearing description when present.
        return desc;
      }
      return desc || this.strings.error_unavailable;
    }

    _showError(message, errorNode, row) {
      if (errorNode) {
        errorNode.textContent = message;
      } else if (row) {
        const rowError = row.querySelector("[data-qo-row-error]");
        if (rowError) rowError.textContent = message;
      } else if (window.MinimogTheme && MinimogTheme.Notification) {
        MinimogTheme.Notification.show({ target: document.body, type: "error", message, last: 3000 });
      }
    }

    _toggleLoading(button, loading) {
      if (!button) return;
      button.classList.toggle("is-loading", loading);
      button.disabled = loading;
    }

    /* --------------------------------------------------------------- money */

    _formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === "function") {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }
      return this._formatMoneyFallback(cents, this.moneyFormat);
    }

    _formatMoneyFallback(cents, format) {
      if (typeof cents === "string") cents = cents.replace(".", "");
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const value = (cents / 100).toFixed(2);
      const parts = value.split(".");
      const withCommas = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
      const amount = withCommas + "." + parts[1];
      const match = format.match(placeholderRegex);
      const token = match ? match[1] : "amount";
      let out;
      switch (token) {
        case "amount_no_decimals":
          out = withCommas;
          break;
        case "amount_with_comma_separator":
          out = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1.") + "," + parts[1];
          break;
        default:
          out = amount;
      }
      return format.replace(placeholderRegex, out);
    }
  }

  customElements.define("quick-order-collection", QuickOrderCollection);
}
